import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
    Box,
    Button,
    Stack,
    Grid,
    Typography,
    CircularProgress,
    CardContent,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Chip,
    Switch,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Save as SaveIcon,
    GroupAdd as GroupAddIcon,
} from '@mui/icons-material';

import { Container, Input, ErrorMessage, InfoLabel, SectionHeader, StyledCard } from '@/components/common';
import { useCreateGroup } from '@/hooks/groups.hook';
import { successToast, errorToast, confirmToast } from '@/common/toast';
import { groups } from '@/constants';
import { validateDateRange } from '@/util/dateUtils';
import { getGroupCourseRequirementIssues } from '@/util/groupCourseRequirements';
import CourseSelectPanel from './components/CourseSelectPanel';
import GroupRequirementAlerts, { CompletionRequirementOptionLabel } from './components/GroupRequirementAlerts';

/**
 * 그룹 등록 페이지 컴포넌트
 * 
 * 단일 그룹을 등록할 수 있는 페이지입니다.
 */
const GroupAddPage = () => {
    const navigate = useNavigate();
    const createGroupMutation = useCreateGroup();

    // 폼 관리
    const {
        control,
        handleSubmit,
        formState: { errors, isDirty },
        watch,
    } = useForm({
        defaultValues: {
            groupName: '',
            groupCourse: '',
            groupCourseCount: '',
            groupStartDate: '',
            groupDueDate: '',
            groupAssessId: '',
            courseIds: [],
            completionRequirements: [],
            aiCodeReviewEnabled: false,
        },
    });

    // 수료 조건 감시 (선택된 항목 표시용)
    const selectedCompletionRequirements = watch('completionRequirements');
    const selectedCourseIds = watch('courseIds');
    const [selectedCourses, setSelectedCourses] = useState([]);
    const selectableCompletionRequirements = groups.getSelectableCompletionRequirements();
    const handleSelectedCoursesChange = useCallback((courses) => {
        setSelectedCourses((prev) => {
            if (
                prev.length === courses.length
                && prev.every((course, index) => (
                    course.courseId === courses[index]?.courseId
                    && Boolean(course.hasTraining) === Boolean(courses[index]?.hasTraining)
                ))
            ) {
                return prev;
            }
            return courses;
        });
    }, []);

    const requirementIssues = useMemo(
        () => getGroupCourseRequirementIssues({
            requirements: selectedCompletionRequirements,
            courseIds: selectedCourseIds,
            selectedCourses,
        }),
        [selectedCompletionRequirements, selectedCourseIds, selectedCourses]
    );

    /**
     * 그룹 등록 핸들러
     */
    const onSubmit = useCallback(
        async (data) => {
            // 날짜 유효성 검사
            if (!validateDateRange(data.groupStartDate, data.groupDueDate)) {
                errorToast('종료일은 시작일보다 이후여야 합니다.');
                return;
            }

            const courseIds = Array.isArray(data.courseIds) ? data.courseIds.map((id) => Number(id)) : [];
            const completionRequirements = groups.sanitizeCompletionRequirements(data.completionRequirements);
            const issues = getGroupCourseRequirementIssues({
                requirements: completionRequirements,
                courseIds,
                selectedCourses,
            });

            if (issues.errors.length > 0) {
                errorToast(issues.errors[0]);
                return;
            }

            const saveGroup = async () => {
                try {
                    const groupData = {
                        groupName: data.groupName,
                        groupCourse: data.groupCourse,
                        groupCourseCount: data.groupCourseCount ? Number(data.groupCourseCount) : 0,
                        groupStartDate: data.groupStartDate,
                        groupDueDate: data.groupDueDate,
                        groupAssessId: data.groupAssessId ? Number(data.groupAssessId) : null,
                        groupStatus: new Date(data.groupStartDate) > new Date() ? 0 : 1,
                        groupCompletePercentage: 80,
                        courseIds,
                        completionRequirements,
                        aiCodeReviewEnabled: data.aiCodeReviewEnabled || false,
                    };

                    await createGroupMutation.mutateAsync(groupData);
                    successToast(`${data.groupName} 그룹이 등록되었습니다.`);
                    navigate('/groups');
                } catch (error) {
                    errorToast(`그룹 등록에 실패했습니다. - ${error.response?.data?.msg}`);
                }
            };

            if (issues.warnings.length > 0) {
                confirmToast(`${issues.warnings[0]} 그래도 등록하시겠습니까?`, () => {
                    saveGroup();
                });
                return;
            }

            await saveGroup();
        },
        [createGroupMutation, navigate, selectedCourses]
    );

    /**
     * 뒤로가기 핸들러
     */
    const handleGoBack = useCallback(() => {
        navigate('/groups');
    }, [navigate]);

    /**
     * 수료 조건 선택 핸들러
     */
    const handleCompletionRequirementToggle = useCallback((contentType, currentList, onChange) => {
        const newList = currentList.includes(contentType)
            ? currentList.filter(type => type !== contentType)
            : [...currentList, contentType];
        onChange(newList);
    }, []);

    return (
        <Container title='그룹 등록'>
            <Stack spacing={3}>
                {/* 상단 버튼 영역 */}
                <Box display='flex' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap={2}>
                    <Button
                        variant='outlined'
                        onClick={handleGoBack}
                        startIcon={<ArrowBackIcon />}
                        sx={{ minWidth: 120 }}
                    >
                        목록으로
                    </Button>
                    <Button
                        variant='contained'
                        onClick={handleSubmit(onSubmit)}
                        startIcon={createGroupMutation.isPending ? <CircularProgress size={16} /> : <SaveIcon />}
                        disabled={!isDirty || createGroupMutation.isPending}
                        sx={{ minWidth: 100 }}
                    >
                        등록
                    </Button>
                </Box>

                {/* 안내 메시지 */}
                <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <GroupAddIcon sx={{ color: '#1976d2' }} />
                        <Typography variant='body2' color='text.secondary'>
                            그룹 정보를 입력하고 등록 버튼을 클릭하여 그룹을 등록할 수 있습니다.
                        </Typography>
                    </Stack>
                </Box>

                <Grid container spacing={3}>
                    {/* 왼쪽: 그룹 기본 정보 */}
                    <Grid item xs={12} md={8}>
                        <StyledCard>
                            <CardContent sx={{ p: 3 }}>
                                <SectionHeader>
                                    <GroupAddIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                                    <Typography variant='h5' fontWeight={600}>
                                        그룹 정보
                                    </Typography>
                                </SectionHeader>

                                <Grid container spacing={3}>
                                    {/* 고객사명 */}
                                    <Grid item xs={12} sm={6}>
                                        <InfoLabel>고객사명 *</InfoLabel>
                                        <Input
                                            control={control}
                                            name='groupName'
                                            textFieldProps={{
                                                fullWidth: true,
                                                required: true,
                                                variant: 'outlined',
                                                placeholder: '고객사명을 입력하세요',
                                            }}
                                            rules={{
                                                required: '고객사명을 입력해주세요.',
                                            }}
                                        />
                                        <ErrorMessage errors={errors} name='groupName' />
                                    </Grid>

                                    {/* 그룹명 */}
                                    <Grid item xs={12} sm={6}>
                                        <InfoLabel>그룹명 *</InfoLabel>
                                        <Input
                                            control={control}
                                            name='groupCourse'
                                            textFieldProps={{
                                                fullWidth: true,
                                                required: true,
                                                variant: 'outlined',
                                                placeholder: '그룹명을 입력하세요',
                                            }}
                                            rules={{
                                                required: '그룹명을 입력해주세요.',
                                            }}
                                        />
                                        <ErrorMessage errors={errors} name='groupCourse' />
                                    </Grid>

                                    {/* 차수 */}
                                    <Grid item xs={12} sm={6}>
                                        <InfoLabel>차수</InfoLabel>
                                        <Input
                                            control={control}
                                            name='groupCourseCount'
                                            textFieldProps={{
                                                fullWidth: true,
                                                type: 'number',
                                                variant: 'outlined',
                                                placeholder: '차수를 입력하세요',
                                            }}
                                        />
                                    </Grid>

                                    {/* 시작일 */}
                                    <Grid item xs={12} sm={6}>
                                        <InfoLabel>시작일 *</InfoLabel>
                                        <Input
                                            control={control}
                                            name='groupStartDate'
                                            textFieldProps={{
                                                fullWidth: true,
                                                type: 'date',
                                                required: true,
                                                variant: 'outlined',
                                                InputLabelProps: {
                                                    shrink: true,
                                                },
                                            }}
                                            rules={{
                                                required: '시작일을 선택해주세요.',
                                            }}
                                        />
                                        <ErrorMessage errors={errors} name='groupStartDate' />
                                    </Grid>

                                    {/* 종료일 */}
                                    <Grid item xs={12} sm={6}>
                                        <InfoLabel>종료일 *</InfoLabel>
                                        <Input
                                            control={control}
                                            name='groupDueDate'
                                            textFieldProps={{
                                                fullWidth: true,
                                                type: 'date',
                                                required: true,
                                                variant: 'outlined',
                                                InputLabelProps: {
                                                    shrink: true,
                                                },
                                            }}
                                            rules={{
                                                required: '종료일을 선택해주세요.',
                                            }}
                                        />
                                        <ErrorMessage errors={errors} name='groupDueDate' />
                                    </Grid>

                                    {/* 평가ID */}
                                    <Grid item xs={12} sm={6}>
                                        <InfoLabel>평가ID</InfoLabel>
                                        <Input
                                            control={control}
                                            name='groupAssessId'
                                            textFieldProps={{
                                                fullWidth: true,
                                                type: 'number',
                                                variant: 'outlined',
                                                placeholder: '평가ID를 입력하세요',
                                            }}
                                        />
                                    </Grid>

                                    {/* AI 코드 리뷰 사용 여부 */}
                                    <Grid item xs={12} sm={6}>
                                        <InfoLabel>프로젝트 개발훈련 사용가능 여부</InfoLabel>
                                        <Controller
                                            control={control}
                                            name="aiCodeReviewEnabled"
                                            render={({ field: { value, onChange } }) => (
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={value || false}
                                                            onChange={(e) => onChange(e.target.checked)}
                                                            color="primary"
                                                        />
                                                    }
                                                    label={value ? '사용' : '미사용'}
                                                />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </StyledCard>
                    </Grid>

                    {/* 오른쪽: 과정 및 수료 조건 선택 */}
                    <Grid item xs={12} md={4}>
                        <StyledCard>
                            <CardContent sx={{ p: 3 }}>
                                <SectionHeader>
                                    <GroupAddIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                                    <Typography variant='h6' fontWeight={600}>
                                        과정 선택
                                    </Typography>
                                </SectionHeader>
                                
                                <CourseSelectPanel
                                    control={control}
                                    name="courseIds"
                                    onSelectedCoursesChange={handleSelectedCoursesChange}
                                />
                                <GroupRequirementAlerts issues={requirementIssues} sx={{ mt: 2 }} />
                            </CardContent>
                            <CardContent sx={{ p: 3 }}>
                                <SectionHeader>
                                    <GroupAddIcon sx={{ color: '#1976d2', fontSize: 28 }} />
                                    <Typography variant='h6' fontWeight={600}>
                                        수료 조건
                                    </Typography>
                                </SectionHeader>

                                <Controller
                                    control={control}
                                    name='completionRequirements'
                                    render={({ field: { value, onChange } }) => (
                                        <FormGroup>
                                            {selectableCompletionRequirements.map((option) => (
                                                <FormControlLabel
                                                    key={option.value}
                                                    sx={{ alignItems: 'flex-start' }}
                                                    control={
                                                        <Checkbox
                                                            disabled={option.disable}
                                                            checked={value.includes(option.value)}
                                                            onChange={() => handleCompletionRequirementToggle(option.value, value, onChange)}
                                                        />
                                                    }
                                                    label={<CompletionRequirementOptionLabel option={option} />}
                                                />
                                            ))}
                                        </FormGroup>
                                    )}
                                />

                                {/* 선택된 수료 조건 표시 */}
                                {selectedCompletionRequirements.length > 0 && (
                                    <Box mt={2}>
                                        <InfoLabel>선택된 수료 조건</InfoLabel>
                                        <Stack direction='row' spacing={1} flexWrap='wrap' gap={1} mt={1}>
                                            {selectedCompletionRequirements.map((contentType) => {
                                                const option = selectableCompletionRequirements.find((opt) => opt.value === contentType);
                                                return option ? (
                                                    <Chip
                                                        key={contentType}
                                                        label={option.label}
                                                        size='small'
                                                        color='secondary'
                                                        variant='outlined'
                                                    />
                                                ) : null;
                                            })}
                                        </Stack>
                                    </Box>
                                )}

                                {selectedCompletionRequirements.length === 0 && (
                                    <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
                                        수료 조건을 선택하지 않았습니다.
                                    </Typography>
                                )}
                            </CardContent>
                        </StyledCard>
                    </Grid>
                </Grid>
            </Stack>
        </Container>
    );
};

export default GroupAddPage;
